import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { Button, cx } from './ui';
import { formatRemaining, playChime, startTimer, stopTimer, useTimer } from '../state/timer';

const PRESETS = [1, 3, 5, 10, 15, 20, 30, 45, 60];

/**
 * App-level timer surface, always mounted so the timer keeps ticking and can chime no matter
 * which view is open. Shows a floating pill while running, a full-width alert when it fires,
 * and a set-a-timer sheet.
 */
export function TimerLayer() {
  const { running, remainingMs, endsAt } = useTimer();
  const [open, setOpen] = useState(false);
  const [alerting, setAlerting] = useState(false);
  const chimedFor = useRef<number | null>(null);

  // Fire exactly once per timer: latch on the endsAt we've already chimed for.
  useEffect(() => {
    if (endsAt !== null && remainingMs === 0 && chimedFor.current !== endsAt) {
      chimedFor.current = endsAt;
      setAlerting(true);
      playChime();
    }
    if (endsAt === null) {
      chimedFor.current = null;
      setAlerting(false);
    }
  }, [endsAt, remainingMs]);

  const dismiss = (): void => {
    stopTimer();
    setAlerting(false);
  };

  return (
    <>
      {/* Floating control: tap to open when idle, shows the countdown when running. */}
      <button
        onClick={() => setOpen(true)}
        className={cx(
          'fixed right-4 bottom-[calc(84px+env(safe-area-inset-bottom))] z-40 flex items-center gap-2 rounded-full border shadow-lg transition-colors md:bottom-4',
          running
            ? 'border-accent bg-surface px-4 py-2.5 text-accent'
            : 'border-border-subtle bg-surface px-3 py-3 text-muted hover:text-primary',
        )}
        aria-label="Kitchen timer"
      >
        <Icon name="timer" size={running ? 16 : 18} />
        {running && <span className="tabular font-mono text-[15px] font-medium">{formatRemaining(remainingMs)}</span>}
      </button>

      {open && <TimerSheet onClose={() => setOpen(false)} />}

      {alerting && (
        <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="animate-pulse text-center">
            <Icon name="timer" size={64} className="mx-auto text-accent" strokeWidth={1.2} />
            <p className="mt-4 font-display text-[32px] text-white">Time's up</p>
          </div>
          <button
            onClick={dismiss}
            className="absolute inset-0"
            aria-label="Dismiss timer"
          />
          <Button variant="primary" size="lg" onClick={dismiss} className="absolute bottom-16 z-10">
            Stop
          </Button>
        </div>
      )}
    </>
  );
}

function TimerSheet({ onClose }: { onClose: () => void }) {
  const { running, remainingMs } = useTimer();
  const [custom, setCustom] = useState('');

  const start = (minutes: number): void => {
    if (minutes > 0) {
      startTimer(minutes * 60_000, `${minutes} min`);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:px-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md overflow-hidden rounded-t-lg border border-border bg-surface-high shadow-[0_20px_60px_rgba(0,0,0,0.45)] sm:rounded-lg">
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
          <div>
            <span className="eyebrow">Kitchen</span>
            <h2 className="font-display text-[18px] text-primary">Timer</h2>
          </div>
          <Icon name="timer" size={20} className="text-muted" />
        </header>

        <div className="p-5">
          {running ? (
            <div className="flex flex-col items-center gap-4">
              <div className="tabular font-mono text-[56px] leading-none font-medium text-accent">
                {formatRemaining(remainingMs)}
              </div>
              <Button variant="danger" size="lg" onClick={() => { stopTimer(); onClose(); }} className="w-40">
                Stop
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-3 gap-2">
                {PRESETS.map((m) => (
                  <button
                    key={m}
                    onClick={() => start(m)}
                    className="touch-lg flex flex-col items-center justify-center rounded-md border border-border-subtle bg-surface transition-colors hover:border-border hover:bg-surface-high"
                  >
                    <span className="tabular font-mono text-[20px] text-primary">{m}</span>
                    <span className="eyebrow">min</span>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <input
                  value={custom}
                  onChange={(e) => setCustom(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  onKeyDown={(e) => e.key === 'Enter' && start(Number(custom))}
                  inputMode="numeric"
                  placeholder="Custom minutes"
                  className="touch flex-1 rounded-md border border-border bg-surface-high px-3 text-[16px] text-primary placeholder:text-muted focus:border-accent focus:outline-none"
                />
                <Button variant="primary" onClick={() => start(Number(custom))} disabled={!custom || Number(custom) < 1}>
                  Start
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
